import coreUtils from '@opentripplanner/core-utils';
import {
  DEFAULT_ROUTE_COLOR,
  WALK_MODE,
  BICYCLE_MODE,
  BICYCLE_RENT_MODE,
  BUS_FX_MODE,
  CAR_HAIL_MODE,
  CAR_MODE,
  CAR_RENT_MODE,
  MICROMOBILITY_MODE,
  MICROMOBILITY_RENT_MODE,
  OTP2_SCOOTER_MODE,
  PARK_AND_RIDE_MODE,
  STREETCAR_GTFS_AGENCY_ID,
  STREETCAR_MODE,
} from '#/shared/constants';

const { isTransit } = coreUtils.itinerary;

/**
 * the GTFS spec indicates that the route color should not have a leading hash
 * symbol, so add one if the routeColor exists and doesn't start with a hash
 * symbol.
 */
export const toSafeRouteColor = routeColor => {
  return (
    routeColor && (routeColor.startsWith('#') ? routeColor : `#${routeColor}`)
  );
};

/**
 * Get color code for transportation mode or transit route
 *
 * @param mode {string} Transportation mode as defined in otp-ui and otp engine
 * @param [routeColor] {string} Hex color code for route from GTFS, only needed
 * when supplied mode is a form on transit
 * @param [defaultColor] {string} Hex color code that is used if there is no
 * handling for the supplied mode and a valid `routeColor` is not supplied
 * @return {string} Hex color code
 */
export const toModeColor = (mode, routeColor = null, defaultColor = null) => {
  switch (mode) {
    case WALK_MODE:
      return '#484848';
    case BICYCLE_MODE:
    case BICYCLE_RENT_MODE:
      return '#ed5b14';
    case CAR_MODE:
    case CAR_HAIL_MODE:
    case CAR_RENT_MODE:
    case PARK_AND_RIDE_MODE:
      return '#808080';
    case MICROMOBILITY_MODE:
    case MICROMOBILITY_RENT_MODE:
    case OTP2_SCOOTER_MODE:
      return '#17c717';
    default:
      return (
        toSafeRouteColor(routeColor) || defaultColor || DEFAULT_ROUTE_COLOR
      );
  }
};

// TODO: https://trimet.atlassian.net/browse/PUBWEB-3578
// Sync up line colors in content panel once colors are established on map
/**
 * Returns attributes for styling an SVG line element that change dynamically
 * based on the supplied mode and routeColor
 * @param {string} mode Transportation mode as defined in otp-ui and otp engine
 * @param {string} routeColor Hex color code for route from GTFS
 * @returns {{ stroke: string, strokeWidth: number, strokeDasharray?: string, strokeLinecap?: "inherit" | "round" | "square" | undefined }}
 */
export const toModeLineStyle = (mode, routeColor) => {
  let modeLineStyle = {};

  if (mode === WALK_MODE) {
    modeLineStyle = {
      stroke: '#92d2f9',
      strokeWidth: 4,
      strokeDasharray: '0 7.5',
      strokeLinecap: 'round',
    };
  } else if (
    [
      BICYCLE_MODE,
      BICYCLE_RENT_MODE,
      CAR_MODE,
      CAR_HAIL_MODE,
      CAR_RENT_MODE,
      PARK_AND_RIDE_MODE,
      MICROMOBILITY_MODE,
      MICROMOBILITY_RENT_MODE,
      OTP2_SCOOTER_MODE,
    ].includes(mode)
  ) {
    modeLineStyle = {
      strokeWidth: 4,
      strokeDasharray: '8 4',
    };
  }

  return {
    strokeWidth: 8,
    stroke: toModeColor(mode, routeColor),
    ...modeLineStyle,
  };
};

/**
 * Get the mode code for the supplied transit itinerary leg
 *
 * @param leg {Object} OpenTripPlanner itinerary leg
 */
export const modeFromLeg = leg => {
  // OTP doesn't distinguish BRT from regular bus so `route` is used to
  // indentify those lines
  if (leg.route === 'FX2') {
    return BUS_FX_MODE;
  }

  // OTP uses "TRAM" for both MAX and Streetcar so `agencyId` is used to
  // detect Streetcar
  if (leg.agencyId === STREETCAR_GTFS_AGENCY_ID) {
    return STREETCAR_MODE;
  }

  return leg.mode;
};

export const modesContainTransit = legs =>
  legs.some(leg => isTransit(leg.mode));
